import { MessageSquare, Mail, Paperclip, Loader2, Search, AlertCircle, Inbox, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Button } from '../ui/button';
import { fmtWhen, initials } from '../../lib/communicationsAPI';

const Row = ({ c, active, checked, onCheck, onOpen }) => (
  <div role="button" tabIndex={0} onClick={() => onOpen(c.id)} onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), onOpen(c.id))}
    data-testid={`comms-row-${c.id}`} aria-current={active ? 'true' : undefined}
    className={cn('flex gap-3 px-3 py-3 border-b border-border cursor-pointer transition-colors focus-visible:outline-none focus-visible:bg-muted/70',
      active ? 'bg-accent/60' : 'hover:bg-muted/50')}>
    <div className="pt-0.5 flex flex-col items-center gap-2" onClick={(e) => e.stopPropagation()}>
      <Checkbox checked={checked} onCheckedChange={(v) => onCheck(c.id, !!v)} aria-label="Select conversation" data-testid={`comms-row-check-${c.id}`} />
      {c.unread && <span className="w-2 h-2 rounded-full bg-primary" data-testid={`comms-row-unread-${c.id}`} aria-label="Unread" />}
    </div>
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-1.5">
        {c.urgent && <AlertTriangle className="w-3.5 h-3.5 text-red-500 shrink-0" aria-label="Urgent" data-testid={`comms-row-urgent-${c.id}`} />}
        <span className={cn('truncate text-sm', c.unread ? 'font-semibold text-foreground' : 'text-foreground/90')}>{c.client_name || 'No client'}</span>
        <span className="ml-auto shrink-0 text-[11px] text-muted-foreground tabular-nums">{fmtWhen(c.last_message_at)}</span>
      </div>
      <p className={cn('truncate text-sm', c.unread ? 'font-medium text-foreground' : 'text-foreground/80')}>{c.subject}</p>
      {c.topic && c.topic.kind !== 'general' && (
        <span className="inline-block mt-0.5 rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground" data-testid={`comms-row-topic-${c.id}`}>{c.topic.label}</span>
      )}
      <div className="flex items-center gap-1.5 mt-0.5 text-xs text-muted-foreground">
        {c.channel === 'email' ? <Mail className="w-3.5 h-3.5 shrink-0" aria-label="Email" /> : <MessageSquare className="w-3.5 h-3.5 shrink-0" aria-label="Client portal" />}
        {c.has_attachments && <Paperclip className="w-3 h-3 shrink-0" aria-label="Has attachments" />}
        <span className="truncate flex-1">{c.last_message_preview}</span>
        <span className={cn('shrink-0 w-6 h-6 rounded-full text-[10px] font-semibold flex items-center justify-center', c.assigned_user_name ? 'bg-primary/10 text-primary' : 'border border-dashed border-border')}
          title={c.assigned_user_name || 'Unassigned'} data-testid={`comms-row-assignee-${c.id}`}>{c.assigned_user_name ? initials(c.assigned_user_name) : ''}</span>
      </div>
    </div>
  </div>
);

export const ConversationList = ({ state, selectedId, onOpen, search, onSearch, checked, onCheck, onLoadMore, toolbar }) => {
  const { rows, loading, error, nextCursor, loadingMore } = state;
  return (
    <section className="flex flex-col min-h-0 border-r border-border w-full md:w-[380px] shrink-0" aria-label="Conversations" data-testid="comms-list">
      <div className="p-2 border-b border-border space-y-2">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => onSearch(e.target.value)} placeholder="Search subject, message or client"
            className="pl-8 h-9" data-testid="comms-search-input" aria-label="Search conversations" />
        </div>
        {toolbar}
      </div>
      <div className="flex-1 overflow-y-auto">
        {loading && !rows.length ? (
          <div className="p-3 space-y-3" data-testid="comms-list-loading">{[0, 1, 2, 3].map((i) => <div key={i} className="h-16 rounded-md bg-muted/60 animate-pulse" />)}</div>
        ) : error ? (
          <div className="p-6 text-center text-sm text-destructive flex flex-col items-center gap-2" data-testid="comms-list-error"><AlertCircle className="w-5 h-5" />{error}</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground flex flex-col items-center gap-2" data-testid="comms-list-empty"><Inbox className="w-8 h-8 opacity-50" /><p className="text-sm">Nothing here.</p></div>
        ) : (
          <>
            {rows.map((c) => <Row key={c.id} c={c} active={c.id === selectedId} checked={checked.has(c.id)} onCheck={onCheck} onOpen={onOpen} />)}
            {nextCursor && (
              <div className="p-3 text-center">
                <Button variant="ghost" size="sm" onClick={onLoadMore} disabled={loadingMore} data-testid="comms-load-more">
                  {loadingMore && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Load more
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};
